import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import { MouseEvent, useState } from 'react';
import { FormatMenuProps } from 'types/DecklineFormatting.types';

/**
 * Generic formatting menu component that provides a dropdown interface for formatting options
 * Used for borders, colors, number formats, and line thickness selections
 *
 * @param props - Configuration object containing button and menu properties
 * @returns JSX element with toggle button and dropdown menu
 */
const FormatMenu = <TValueEnum,>({
  buttonValue,
  buttonAriaLabel,
  buttonIcon,
  options,
  onFormatSelect,
  selectedValue,
  selectedValues,
  tooltip,
  isEditingDisabled,
}: FormatMenuProps<TValueEnum>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /**
   * Handles click events on the toggle button to open the dropdown menu
   * @param event - Mouse event from the button click
   */
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handles selection of a menu option and closes the dropdown
   * @param formatType - The value of the selected formatting option
   */
  const handleSelect = (formatType: TValueEnum) => {
    onFormatSelect(formatType);
    setAnchorEl(null);
  };

  /**
   * Toggle button component for the formatting menu
   */
  const toggleButton = (
    <ToggleButton
      value={buttonValue}
      aria-label={buttonAriaLabel}
      // className='formatting-group'
      onClick={handleClick}
      size='small'
      disabled={isEditingDisabled}
    >
      {buttonIcon}
    </ToggleButton>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Box component='span' className='format-button-wrapper'>
            {toggleButton}
          </Box>
        </Tooltip>
      ) : (
        toggleButton
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {options.map((option) => {
          // A multi-select menu (borders) reports its own set; everything else compares the single
          // selected value. The previous cast to CellFormat made this compile for every TValueEnum
          // while only ever matching the number-format menu.
          const isSelected = selectedValues ? selectedValues.includes(option.value) : selectedValue === option.value;
          return (
            <MenuItem key={option.value?.toString()} onClick={() => handleSelect(option.value)} selected={isSelected}>
              {option.icon}
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default FormatMenu;
